import { defineStore } from 'pinia';
import apiClient from '../utils/request';

export interface UserProfile {
  id: string;
  nickname: string;
  avatar: string;
  phone: string;
  city: string;
  verified?: boolean;
  scanCount: number;
}

export interface ScanRecord {
  code: string;
  productName: string;
  brandName: string;
  scannedAt: string;
  isAuthentic: boolean;
}

const STORAGE_KEY_USER = 'beimi_c_user_profile';
const STORAGE_KEY_TOKEN = 'beimi_c_auth_token';
const STORAGE_KEY_DEVICE_ID = 'beimi_c_device_id';

export const useUserStore = defineStore('user', {
  state: () => {
    let cachedUser: UserProfile | null = null;
    let token: string | null = null;
    try {
      const uStr = localStorage.getItem(STORAGE_KEY_USER);
      if (uStr) cachedUser = JSON.parse(uStr);
      token = localStorage.getItem(STORAGE_KEY_TOKEN);
    } catch {}

    return {
      token: token || (null as string | null),
      isLoggedIn: !!token,
      user: (cachedUser || {
        id: 'u_c_001',
        nickname: '微信绿色生活家',
        avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80',
        phone: '138****8899',
        city: '黑龙江 · 哈尔滨',
        verified: true,
        scanCount: 1,
      }) as UserProfile | null,
      scanHistory: [
        {
          code: 'MA.156.A1.0058.AG.101/20260812.ORG.0010',
          productName: '5kg 有机胚芽米六角真空礼盒装',
          brandName: '北米臻选 · 有机胚芽米',
          scannedAt: '2026-08-28 09:05:00',
          isAuthentic: true,
        },
      ] as ScanRecord[],
    };
  },

  actions: {
    /**
     * 客户端设备指纹免密静默登录（零微信资质依赖，扫码直达）
     */
    async silentLogin(customDeviceId?: string) {
      try {
        let deviceId = customDeviceId || localStorage.getItem(STORAGE_KEY_DEVICE_ID);
        if (!deviceId) {
          deviceId = `dev_${Date.now().toString(36)}_${Math.random().toString(36).substring(2, 7)}`;
          localStorage.setItem(STORAGE_KEY_DEVICE_ID, deviceId);
        }

        const res = await apiClient.post<any, any>('/c/auth/anonymous-login', {
          deviceId,
        });

        if (res && res.token) {
          this.token = res.token;
          this.isLoggedIn = true;
          this.user = res.user;
          localStorage.setItem(STORAGE_KEY_TOKEN, this.token || '');
          localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(this.user));
          return res;
        }
      } catch (err) {
        console.warn('设备免密静默登录提示 (使用本地兜底):', err);
        // 网络异常或离线时，本地模拟签发兜底会话保障不阻断页面渲染
        if (!this.token) {
          this.token = `mock_token_${Date.now()}`;
          this.isLoggedIn = true;
          localStorage.setItem(STORAGE_KEY_TOKEN, this.token);
        }
      }
    },

    async loginWithWechat(code?: string, userInfo?: { nickname: string; avatarUrl: string }) {
      try {
        const res = await apiClient.post<any, any>('/c/auth/wechat-login', {
          code: code || `wx_code_${Date.now()}`,
          userInfo: userInfo || {
            nickname: '北米有机探索者',
            avatarUrl: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120',
          },
        });
        if (res && res.token) {
          this.token = res.token;
          this.isLoggedIn = true;
          this.user = res.user;
          localStorage.setItem(STORAGE_KEY_TOKEN, this.token || '');
          localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(this.user));
          return res;
        }
      } catch (err) {
        console.error('微信快捷登录失败:', err);
        throw err;
      }
    },

    async loginWithPhone(phone: string, smsCode: string = '888888') {
      try {
        const res = await apiClient.post<any, any>('/c/auth/phone-login', {
          phone,
          smsCode,
        });
        if (res && res.token) {
          this.token = res.token;
          this.isLoggedIn = true;
          this.user = res.user;
          localStorage.setItem(STORAGE_KEY_TOKEN, this.token || '');
          localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(this.user));
          return res;
        }
      } catch (err) {
        console.error('手机号验证登录失败:', err);
        throw err;
      }
    },

    async sendSms(phone: string) {
      return apiClient.post('/c/auth/send-sms', { phone });
    },

    addScanRecord(record: ScanRecord) {
      this.scanHistory.unshift(record);
      if (this.user) {
        this.user.scanCount = (this.user.scanCount || 0) + 1;
        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(this.user));
      }
    },

    logout() {
      this.token = null;
      this.isLoggedIn = false;
      this.user = null;
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      localStorage.removeItem(STORAGE_KEY_USER);
    },
  },
});
